import React, { useState, useEffect, useMemo } from "react";
import {
  weeklyPenaltiesService,
  usersService,
  investmentsService,
} from "../services/api";
import {
  formatCurrency,
  toPersianNumbers,
  toEnglishNumbers,
  formatJalaliDate,
} from "../utils/numbers";
import {
  Plus,
  Search,
  Trash2,
  Coins,
  AlertTriangle,
  RefreshCw,
  X,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import { Modal } from "../components/Modal";
import JalaliDatePicker from "../components/JalaliDatePicker";
import { SearchableSelect } from "../components/SearchableSelect";

interface WeeklyPenalty {
  id: number;
  amount: string | number;
  investment_date: string;
  week: number;
  created_at?: string;
  description?: string;
  penalized_user_id?: number;
  user_first_name?: string;
  user_last_name?: string;
  user_national_code?: string;
  user_mobile?: string;
}

interface UserOption {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
  national_code?: string;
  mobile?: string;
  is_active?: number | boolean;
}

export default function WeeklyPenalties() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  const [penalties, setPenalties] = useState<WeeklyPenalty[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [maxSystemWeek, setMaxSystemWeek] = useState<number>(1);

  // Form State
  const [formData, setFormData] = useState({
    user_id: "",
    amount: "",
    week: "",
    penalty_date: "",
    description: "",
  });

  // Search & Filter State
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedWeekFilter, setSelectedWeekFilter] = useState<string>("all");

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Modal Alert / Confirm State
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "warning" | "info" | "confirm";
    onConfirm?: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [penaltiesData, usersData, investmentsData] = await Promise.all([
        weeklyPenaltiesService.getWeeklyPenalties().catch(() => []),
        usersService.getUsers().catch(() => []),
        investmentsService.getInvestments().catch(() => []),
      ]);

      setPenalties(penaltiesData || []);

      // Filter active users
      const usersList = Array.isArray(usersData)
        ? usersData
        : usersData?.users || [];
      const activeUsers = usersList.filter(
        (u: UserOption) =>
          u.id !== 20 && // Don't penalize user 20 (it's the recipient)
          (u.is_active === 1 ||
            u.is_active === undefined ||
            u.is_active === true),
      );
      setUsers(activeUsers);

      // Determine latest week
      let latestWeek = 1;
      if (Array.isArray(investmentsData) && investmentsData.length > 0) {
        latestWeek = Math.max(
          ...investmentsData.map((inv: any) => Number(inv.week || 0)),
          1,
        );
      }
      setMaxSystemWeek(latestWeek);
    } catch (error) {
      console.error("Error loading weekly penalties data:", error);
      setModalState({
        isOpen: true,
        title: "خطا در بارگذاری",
        message: "خطا در دریافت اطلاعات جریمه‌های هفتگی و کاربران رخ داد.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getTodayJalali = () => {
    try {
      const today = new Date();
      const parts = new Intl.DateTimeFormat("fa-IR-u-nu-latn", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(today);
      return parts.replace(/\//g, "/");
    } catch (e) {
      return "1405/01/01";
    }
  };

  const handleOpenAddModal = () => {
    const todayJalali = getTodayJalali();
    setFormData({
      user_id: "",
      amount: "",
      week: String(maxSystemWeek || 1),
      penalty_date: todayJalali,
      description: "",
    });
    setIsAddModalOpen(true);
  };

  const handleUserSelect = (userId: string) => {
    const selectedUser = users.find((u) => String(u.id) === String(userId));
    const targetName = selectedUser
      ? `${selectedUser.first_name} ${selectedUser.last_name}`
      : "";
    const weekNum = formData.week || maxSystemWeek || 1;

    setFormData((prev) => ({
      ...prev,
      user_id: userId,
      description: targetName
        ? `جریمه دیرکرد واریز هفتگی - هفته ${weekNum} (${targetName})`
        : prev.description,
    }));
  };

  const handleWeekChange = (newWeek: string) => {
    setFormData((prev) => {
      const selectedUser = users.find(
        (u) => String(u.id) === String(prev.user_id),
      );
      const targetName = selectedUser
        ? `${selectedUser.first_name} ${selectedUser.last_name}`
        : "";
      return {
        ...prev,
        week: newWeek,
        description: targetName
          ? `جریمه دیرکرد واریز هفتگی - هفته ${newWeek} (${targetName})`
          : prev.description,
      };
    });
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = toEnglishNumbers(e.target.value).replace(/[^0-9]/g, "");
    setFormData((prev) => ({
      ...prev,
      amount: raw,
    }));
  };

  const handleSubmitPenalty = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.user_id) {
      setModalState({
        isOpen: true,
        title: "خطای اعتبارسنجی",
        message: "لطفاً کاربر مشمول جریمه را انتخاب نمایید.",
        type: "warning",
      });
      return;
    }

    const numericAmount = Number(formData.amount);
    if (!numericAmount || numericAmount <= 0) {
      setModalState({
        isOpen: true,
        title: "خطای اعتبارسنجی",
        message: "لطفاً مبلغ جریمه را به درستی وارد کنید.",
        type: "warning",
      });
      return;
    }

    if (!formData.penalty_date) {
      setModalState({
        isOpen: true,
        title: "خطای اعتبارسنجی",
        message: "لطفاً تاریخ ثبت جریمه را وارد کنید.",
        type: "warning",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await weeklyPenaltiesService.createWeeklyPenalty({
        user_id: Number(formData.user_id),
        amount: numericAmount,
        penalty_date: formData.penalty_date,
        week: Number(formData.week || maxSystemWeek || 1),
        description: formData.description.trim(),
      });

      setIsAddModalOpen(false);
      fetchData();

      setModalState({
        isOpen: true,
        title: "عملیات موفق",
        message:
          response.message ||
          "جریمه هفتگی با موفقیت ثبت و به حساب ارزش افزوده نهام (کاربر شماره ۲۰) واریز شد.",
        type: "success",
      });
    } catch (error: any) {
      console.error("Error creating weekly penalty:", error);
      setModalState({
        isOpen: true,
        title: "خطا در ثبت جریمه",
        message:
          error.response?.data?.message ||
          "خطایی در سرور هنگام ثبت جریمه رخ داد.",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePenalty = (penalty: WeeklyPenalty) => {
    const penalizedName = penalty.user_first_name
      ? `${penalty.user_first_name} ${penalty.user_last_name}`
      : `کاربر #${penalty.penalized_user_id}`;

    setModalState({
      isOpen: true,
      title: "تایید حذف جریمه هفتگی",
      message: `آیا از حذف جریمه هفتگی ${penalizedName} به مبلغ ${formatCurrency(penalty.amount)} اطمینان دارید؟ این رکورد از ارزش افزوده کاربر ۲۰ کسر خواهد شد.`,
      type: "confirm",
      onConfirm: async () => {
        try {
          setModalState((prev) => ({ ...prev, isOpen: false }));
          await weeklyPenaltiesService.deleteWeeklyPenalty(penalty.id);
          setPenalties((prev) => prev.filter((p) => p.id !== penalty.id));
          setModalState({
            isOpen: true,
            title: "عملیات موفق",
            message: "جریمه هفتگی با موفقیت حذف گردید.",
            type: "success",
          });
        } catch (err: any) {
          console.error("Error deleting penalty:", err);
          setModalState({
            isOpen: true,
            title: "خطا",
            message: err.response?.data?.message || "خطا در حذف جریمه هفتگی.",
            type: "error",
          });
        }
      },
    });
  };

  // Filtered and searched penalties
  const filteredPenalties = useMemo(() => {
    return penalties.filter((p) => {
      // Filter by week
      if (
        selectedWeekFilter !== "all" &&
        String(p.week) !== selectedWeekFilter
      ) {
        return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        const fullName =
          `${p.user_first_name || ""} ${p.user_last_name || ""}`.toLowerCase();
        const nationalCode = (p.user_national_code || "").toLowerCase();
        const mobile = (p.user_mobile || "").toLowerCase();
        const desc = (p.description || "").toLowerCase();
        const weekStr = String(p.week);

        return (
          fullName.includes(query) ||
          nationalCode.includes(query) ||
          mobile.includes(query) ||
          desc.includes(query) ||
          weekStr.includes(query)
        );
      }

      return true;
    });
  }, [penalties, searchQuery, selectedWeekFilter]);

  // Statistics
  const totalPenaltiesAmount = useMemo(() => {
    return penalties.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  }, [penalties]);

  const uniqueWeeks = useMemo(() => {
    const set = new Set<number>();
    penalties.forEach((p) => {
      if (p.week) set.add(Number(p.week));
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [penalties]);

  // Pagination
  const totalPages = Math.ceil(filteredPenalties.length / itemsPerPage);
  const paginatedPenalties = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredPenalties.slice(start, start + itemsPerPage);
  }, [filteredPenalties, currentPage, itemsPerPage]);

  const userSelectOptions = useMemo(() => {
    return users.map((u) => ({
      value: String(u.id),
      label: `${u.first_name} ${u.last_name} ${u.national_code ? `(${toPersianNumbers(u.national_code)})` : ""} ${u.role === "investor" ? "[سرمایه‌گذار]" : ""}`,
    }));
  }, [users]);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
              <Coins className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                جریمه‌های هفتگی
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                ثبت جریمه‌های دیرکرد پرداخت هفتگی و واریز مستقیم به حساب ارزش
                افزوده نهام (کاربر شماره ۲۰)
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          {!isAdmin && (
            <span className="px-3.5 py-2 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 rounded-xl text-xs font-semibold">
              حالت فقط مشاهده (سرمایه‌گذار)
            </span>
          )}

          <button
            onClick={fetchData}
            title="بروزرسانی داده‌ها"
            className="p-2.5 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
          </button>

          {isAdmin && (
            <button
              onClick={handleOpenAddModal}
              className="flex-1 sm:flex-initial flex items-center justify-center gap-2 px-5 py-2.5 bg-persian-600 text-white rounded-xl hover:bg-persian-700 transition-colors shadow-lg shadow-persian-600/20 font-medium"
            >
              <Plus size={20} />
              <span>ثبت جریمه هفتگی جدید</span>
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Penalties Amount */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200/80 dark:border-slate-700 flex flex-col justify-between relative overflow-hidden">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
              مجموع کل جریمه‌های هفتگی
            </span>
            <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 flex items-center justify-center">
              <Coins className="w-5 h-5" />
            </div>
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">
              {formatCurrency(totalPenaltiesAmount)}
            </p>
            {totalPenaltiesAmount > 0 && (
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                معادل{" "}
                {toPersianNumbers(
                  Math.floor(totalPenaltiesAmount / 10).toLocaleString(),
                )}{" "}
                تومان
              </p>
            )}
          </div>
        </div>

        {/* Total Count */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200/80 dark:border-slate-700 flex flex-col justify-between relative overflow-hidden">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
              تعداد جریمه‌های ثبت‌شده
            </span>
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5" />
            </div>
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">
              {toPersianNumbers(penalties.length)}{" "}
              <span className="text-sm font-normal text-slate-500">فقره</span>
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
              در {toPersianNumbers(uniqueWeeks.length)} هفته مختلف
            </p>
          </div>
        </div>

        {/* Recipient Account Notice */}
        <div className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-6 shadow-lg shadow-emerald-600/20 text-white flex flex-col justify-between relative overflow-hidden">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-emerald-100">
              مقصد واریز جریمه‌ها
            </span>
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white">
              <ShieldCheck className="w-5 h-5" />
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-xl font-bold text-white">ارزش افزوده نهام</p>
              <span className="px-2 py-0.5 rounded-md bg-white/20 text-xs font-semibold">
                کاربر ۲۰
              </span>
            </div>
            <p className="text-xs text-emerald-100/90 mt-1.5 leading-relaxed">
              تمامی جریمه‌های هفتگی به صورت مستقیم به عنوان رکورد سرمایه‌گذاری
              ارزش افزوده برای کاربر ۲۰ ثبت می‌شوند.
            </p>
          </div>
        </div>
      </div>

      {/* Filters & Actions Bar */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 shadow-sm border border-slate-200/80 dark:border-slate-700 flex flex-col sm:flex-row gap-4 items-center justify-between">
        {/* Search */}
        <div className="relative w-full sm:w-80">
          <Search className="w-5 h-5 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="جستجوی نام کاربر، کدملی، هفته..."
            className="w-full pr-10 pl-4 py-2.5 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-persian-500 outline-none dark:text-white"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Week Filter */}
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <span className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
            فیلتر هفته:
          </span>
          <select
            value={selectedWeekFilter}
            onChange={(e) => setSelectedWeekFilter(e.target.value)}
            className="w-full sm:w-44 px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-persian-500 outline-none dark:text-white"
          >
            <option value="all">همه هفته‌ها</option>
            {uniqueWeeks.map((w) => (
              <option key={w} value={String(w)}>
                هفته {toPersianNumbers(w)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table of Weekly Penalties */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200/80 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-right">
            <thead>
              <tr className="bg-slate-50/70 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-700">
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  ردیف
                </th>
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  کاربر مشمول جریمه
                </th>
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  شماره هفته
                </th>
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  مبلغ جریمه
                </th>
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  تاریخ ثبت
                </th>
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  بابت / توضیحات
                </th>
                <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400">
                  واریز به
                </th>
                {isAdmin && (
                  <th className="p-4 text-sm font-medium text-slate-500 dark:text-slate-400 text-center">
                    عملیات
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700 text-sm">
              {loading ? (
                <tr>
                  <td colSpan={isAdmin ? 8 : 7} className="p-12 text-center">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-persian-500"></div>
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        در حال بارگذاری اطلاعات جریمه‌ها...
                      </span>
                    </div>
                  </td>
                </tr>
              ) : paginatedPenalties.length > 0 ? (
                paginatedPenalties.map((penalty, index) => (
                  <tr
                    key={penalty.id}
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                  >
                    {/* Index */}
                    <td className="p-4 font-medium text-slate-900 dark:text-white">
                      {toPersianNumbers(
                        (currentPage - 1) * itemsPerPage + index + 1,
                      )}
                    </td>

                    {/* Penalized User Info */}
                    <td className="p-4">
                      <div className="flex flex-col">
                        <span className="font-semibold text-slate-900 dark:text-white">
                          {penalty.user_first_name
                            ? `${penalty.user_first_name} ${penalty.user_last_name}`
                            : `کاربر #${penalty.penalized_user_id || "-"}`}
                        </span>
                        <div className="flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                          {penalty.user_national_code && (
                            <span>
                              کد ملی:{" "}
                              {toPersianNumbers(penalty.user_national_code)}
                            </span>
                          )}
                          {penalty.user_mobile && (
                            <span>
                              | موبایل: {toPersianNumbers(penalty.user_mobile)}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Week Number */}
                    <td className="p-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300">
                        هفته {toPersianNumbers(penalty.week || 1)}
                      </span>
                    </td>

                    {/* Penalty Amount */}
                    <td className="p-4">
                      <div className="flex flex-col">
                        <span className="font-bold text-amber-600 dark:text-amber-400">
                          {formatCurrency(penalty.amount)}
                        </span>
                        <span className="text-xs text-slate-400 dark:text-slate-500">
                          {toPersianNumbers(
                            Math.floor(
                              Number(penalty.amount) / 10,
                            ).toLocaleString(),
                          )}{" "}
                          تومان
                        </span>
                      </div>
                    </td>

                    {/* Date */}
                    <td className="p-4" dir="ltr">
                      <span className="font-medium text-slate-700 dark:text-slate-300">
                        {formatJalaliDate(penalty.investment_date)}
                      </span>
                    </td>

                    {/* Description */}
                    <td className="p-4 max-w-xs">
                      <p className="text-slate-700 dark:text-slate-300 line-clamp-2 text-xs leading-relaxed">
                        {penalty.description || "جریمه دیرکرد هفتگی"}
                      </p>
                    </td>

                    {/* Deposited To */}
                    <td className="p-4">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/30">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>ارزش افزوده (کاربر ۲۰)</span>
                      </span>
                    </td>

                    {/* Actions */}
                    {isAdmin && (
                      <td className="p-4 text-center">
                        <button
                          onClick={() => handleDeletePenalty(penalty)}
                          className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="حذف جریمه"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={isAdmin ? 8 : 7} className="p-12 text-center">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="p-3 rounded-2xl bg-slate-100 dark:bg-slate-700/50 text-slate-400">
                        <Coins className="w-8 h-8" />
                      </div>
                      <p className="text-base font-semibold text-slate-800 dark:text-slate-200">
                        هیچ جریمه هفتگی یافت نشد.
                      </p>
                      {isAdmin && (
                        <>
                          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm">
                            در صورت دیرکرد در واریز هفتگی کاربران، می‌توانید از
                            طریق دکمه «ثبت جریمه هفتگی جدید» اقدام کنید.
                          </p>
                          <button
                            onClick={handleOpenAddModal}
                            className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-persian-600 text-white rounded-xl hover:bg-persian-700 transition-colors text-sm font-medium shadow-md shadow-persian-600/20"
                          >
                            <Plus size={16} />
                            <span>ثبت اولین جریمه هفتگی</span>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              نمایش صفحه {toPersianNumbers(currentPage)} از{" "}
              {toPersianNumbers(totalPages)} (مجموع{" "}
              {toPersianNumbers(filteredPenalties.length)} مورد)
            </span>
            <div className="flex items-center gap-1.5">
              <button
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 dark:border-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-slate-700 dark:text-slate-300"
              >
                قبلی
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${
                      currentPage === page
                        ? "bg-persian-600 text-white"
                        : "border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300"
                    }`}
                  >
                    {toPersianNumbers(page)}
                  </button>
                ),
              )}
              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-200 dark:border-slate-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-slate-700 dark:text-slate-300"
              >
                بعدی
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Weekly Penalty Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-700 w-full max-w-lg overflow-hidden flex flex-col max-h-[92vh] animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="p-5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
                  <Coins className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                    ثبت جریمه هفتگی جدید
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    جریمه دیرکرد پرداخت هفتگی و واریز به ارزش افزوده (کاربر ۲۰)
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form
              onSubmit={handleSubmitPenalty}
              className="p-6 overflow-y-auto space-y-4 flex-1"
            >
              {/* Highlight Info Box */}
              <div className="p-3.5 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/40 rounded-2xl flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                <div className="text-xs text-emerald-800 dark:text-emerald-200 leading-relaxed">
                  <span className="font-bold">
                    واریز مستقیم به حساب ارزش افزوده:
                  </span>{" "}
                  این جریمه به عنوان یک رکورد در سرمایه‌گذاری ارزش افزوده برای{" "}
                  <strong>کاربر شماره ۲۰ (ارزش افزوده نهام)</strong> ثبت و ذخیره
                  می‌شود.
                </div>
              </div>

              {/* User Selection */}
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                  انتخاب کاربر مشمول جریمه *
                </label>
                <SearchableSelect
                  options={userSelectOptions}
                  value={formData.user_id}
                  onChange={handleUserSelect}
                  placeholder="جستجو و انتخاب کاربر مشمول جریمه..."
                  required
                />
              </div>

              {/* Week & Date Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                    شماره هفته *
                  </label>
                  <input
                    type="number"
                    dir="ltr"
                    min="1"
                    value={formData.week}
                    onChange={(e) => handleWeekChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-persian-500 outline-none dark:text-white text-left font-bold"
                    placeholder="مثلاً 43"
                    required
                  />
                </div>

                <div>
                  <JalaliDatePicker
                    label="تاریخ ثبت جریمه *"
                    value={formData.penalty_date}
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, penalty_date: val }))
                    }
                    required
                  />
                </div>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                  مبلغ جریمه (ریال) *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    dir="ltr"
                    value={
                      formData.amount
                        ? Number(formData.amount).toLocaleString()
                        : ""
                    }
                    onChange={handleAmountChange}
                    className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-persian-500 outline-none dark:text-white font-bold font-bold text-left"
                    placeholder="مثلاً 500,000"
                    required
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                    ریال
                  </span>
                </div>
                {formData.amount && Number(formData.amount) > 0 && (
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    معادل:{" "}
                    {toPersianNumbers(
                      Math.floor(Number(formData.amount) / 10).toLocaleString(),
                    )}{" "}
                    تومان
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                  بابت / توضیحات
                </label>
                <textarea
                  rows={2}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-persian-500 outline-none dark:text-white resize-none"
                  placeholder="علت یا بابت جریمه دیرکرد هفتگی..."
                />
              </div>

              {/* Modal Actions */}
              <div className="pt-4 flex gap-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 flex items-center justify-center gap-2 bg-persian-600 hover:bg-persian-700 disabled:opacity-50 text-white h-11 rounded-xl font-medium transition-colors shadow-lg shadow-persian-600/20"
                >
                  {isSubmitting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  ) : (
                    <>
                      <Plus className="w-4 h-4" />
                      <span>ثبت و واریز به کاربر ۲۰</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => setIsAddModalOpen(false)}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-200 h-11 rounded-xl font-medium transition-colors"
                >
                  انصراف
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Alert / Confirm Notification */}
      <Modal
        isOpen={modalState.isOpen}
        onClose={() => setModalState((prev) => ({ ...prev, isOpen: false }))}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        onConfirm={modalState.onConfirm}
      />
    </div>
  );
}
